import { Modal, Checkbox, Button, notification } from "antd";
import jsPDF from "jspdf";
import { useState, useRef, useEffect } from "react";
import moment from "moment";
import { ToWords } from "to-words";
import QRCode from "qrcode";
import {
  getStateFromGST,
  getStateCodeFromStateName,
} from "../../../../../utils/getStateFromGST";
import { BsDownload } from "react-icons/bs";
import { BsEye } from "react-icons/bs";
import { FaFileExcel } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import generateAnnexureExcel from "./GenerateAnnexureExcel";
import ReactQuill from "react-quill";
import { useSelector } from "react-redux";
import "react-quill/dist/quill.snow.css";
import convertS3ToCloudFront, {
  getViewableFileUrl,
} from "../../../../../utils/convertS3ToCloudFront";

const DEFAULT_INVOICE_REMARK = "";

/** DD-MM-YYYY for PDF cells. Moment's .format() on invalid input returns the string "Invalid date". */
function formatDateForPdfDdMmYyyy(value, fallback) {
  const tryOne = (v) => {
    if (v == null || v === "") return "";
    if (typeof v === "object" && v !== null && "$date" in v) {
      const m = moment(v.$date);
      return m.isValid() ? m.format("DD-MM-YYYY") : "";
    }
    // API often sends bdfDate as "DD-MM-YYYY" (e.g. "01-01-2025"). Moment's default parser is unreliable for that.
    if (typeof v === "string") {
      const dmy = moment(v, "DD-MM-YYYY", true);
      if (dmy.isValid()) return dmy.format("DD-MM-YYYY");
    }
    const m = moment(v);
    return m.isValid() ? m.format("DD-MM-YYYY") : "";
  };
  return tryOne(value) || tryOne(fallback) || "";
}

function trimAddressPart(v) {
  if (v == null) return "";
  return String(v).trim();
}

function pdfFieldOrNa(value) {
  if (value == null) return "NA";
  const s = String(value).trim();
  return s === "" ? "NA" : s;
}

function resolvePartyStateFromInvoiceData(invoiceData) {
  const candidates = [
    invoiceData?.partyState,
    invoiceData?.state,
    invoiceData?.splitState,
  ];
  for (const value of candidates) {
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return null;
}

function resolvePartyPanFromInvoiceData(invoiceData) {
  const candidates = [
    invoiceData?.partyPanNumber,
    invoiceData?.panNumber,
    invoiceData?.splitPanNumber,
  ];
  for (const value of candidates) {
    if (value != null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return null;
}

/** GST present → derive state/code/PAN from GST; else party fields or NA (B2C). */
function resolvePdfPartyTaxFields(invoiceData, gstNumber, hasValidGstNumber) {
  const partyState = resolvePartyStateFromInvoiceData(invoiceData);
  const partyPan = resolvePartyPanFromInvoiceData(invoiceData);

  if (hasValidGstNumber) {
    const stateNameFromGst = getStateFromGST(gstNumber);
    const stateName =
      stateNameFromGst &&
      stateNameFromGst !== "NA" &&
      stateNameFromGst !== "Invalid GST Number"
        ? stateNameFromGst
        : pdfFieldOrNa(partyState);
    const panFromGst =
      gstNumber.length >= 15 ? gstNumber.substring(2, 12) : null;
    return {
      gstDisplay: gstNumber,
      stateName,
      stateCode: gstNumber.substring(0, 2),
      panNumber: pdfFieldOrNa(panFromGst || partyPan),
      placeOfSupply: stateName,
    };
  }

  const stateName = pdfFieldOrNa(partyState);
  const stateCodeFromName =
    stateName !== "NA" ? getStateCodeFromStateName(stateName) : "NA";

  return {
    gstDisplay: "NA",
    stateName,
    stateCode: stateCodeFromName,
    panNumber: pdfFieldOrNa(partyPan),
    placeOfSupply: stateName,
  };
}

/** Prefer split billing party when it has structured lines or a legacy address string. */
function hasSplitPartyAddressData(inv) {
  if (
    [
      inv?.splitAdd1,
      inv?.splitAdd2,
      inv?.splitAdd3,
      inv?.splitCity,
      inv?.splitPinCode,
    ].some((x) => trimAddressPart(x))
  ) {
    return true;
  }
  return Boolean(trimAddressPart(inv?.splitParentCompanyAddress));
}

/**
 * Three logical lines: "add1,", "add2,", "add3, city - pinCode" (commas per spec; line 3 omits pieces if absent).
 * Falls back to legacy single address (wrapped then folded to three segments), then "N/A".
 */
function hasPartyAddressData(inv) {
  if (
    [
      inv?.partyAdd1,
      inv?.partyAdd2,
      inv?.partyAdd3,
      inv?.partyCity,
      inv?.partyPinCode,
    ].some((x) => trimAddressPart(x))
  ) {
    return true;
  }
  return Boolean(trimAddressPart(inv?.partyAddress));
}

function buildPartyAddressThreeLinesForPdf(doc, invoiceData, wrapWidthMm) {
  const useParty = hasPartyAddressData(invoiceData);
  const useSplit = !useParty && hasSplitPartyAddressData(invoiceData);
  const add1 = useParty
    ? trimAddressPart(invoiceData?.partyAdd1)
    : useSplit
      ? trimAddressPart(invoiceData?.splitAdd1)
      : trimAddressPart(invoiceData?.parentAdd1);
  const add2 = useParty
    ? trimAddressPart(invoiceData?.partyAdd2)
    : useSplit
      ? trimAddressPart(invoiceData?.splitAdd2)
      : trimAddressPart(invoiceData?.parentAdd2);
  const add3 = useParty
    ? trimAddressPart(invoiceData?.partyAdd3)
    : useSplit
      ? trimAddressPart(invoiceData?.splitAdd3)
      : trimAddressPart(invoiceData?.parentAdd3);
  const city = useParty
    ? trimAddressPart(invoiceData?.partyCity)
    : useSplit
      ? trimAddressPart(invoiceData?.splitCity)
      : trimAddressPart(invoiceData?.parentCity);
  const pinCode = useParty
    ? trimAddressPart(invoiceData?.partyPinCode)
    : useSplit
      ? trimAddressPart(invoiceData?.splitPinCode)
      : trimAddressPart(invoiceData?.parentPinCode);
  const legacy = useParty
    ? trimAddressPart(invoiceData?.partyAddress)
    : useSplit
      ? trimAddressPart(invoiceData?.splitParentCompanyAddress)
      : trimAddressPart(invoiceData?.parentCompanyAddress);

  const hasStructured = Boolean(add1 || add2 || add3 || city || pinCode);

  if (hasStructured) {
    const line1 = add1 ? `${add1},` : "";
    const line2 = add2 ? `${add2},` : "";
    const cityPin = [city, pinCode].filter(Boolean).join(" - ");
    const line3Parts = [];
    if (add3) line3Parts.push(add3);
    if (cityPin) line3Parts.push(cityPin);
    const line3 = line3Parts.join(", ");
    return [line1, line2, line3];
  }

  if (legacy) {
    const wrapped = doc.splitTextToSize(legacy, wrapWidthMm);
    const l1 = wrapped[0] || "";
    const l2 = wrapped[1] || "";
    const l3 = wrapped.length > 2 ? wrapped.slice(2).join(" ") : "";
    if (l1 || l2 || l3) {
      return [l1 || "N/A", l2, l3];
    }
  }

  return ["N/A", "", ""];
}

const GenerateSubInvoice = ({
  isOpen,
  onClose,
  invoiceData,
  eInvoice = false,
}) => {


  console.log("invoiceData:::", invoiceData);
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const [showHeading, setShowHeading] = useState(true);
  const [showPeriod, setShowPeriod] = useState(true);
  const [showZeroAmountInventories, setShowZeroAmountInventories] =
    useState(false);
  const [showItemAmount, setShowItemAmount] = useState(false);
  const [showLogo, setShowLogo] = useState(true);
  const [asPerAnnexure, setAsPerAnnexure] = useState(false);
  const [showSeparateProductionCharges, setShowSeparateProductionCharges] =
    useState(false);
  const [showHoardingWisePrintingMounting, setShowHoardingWisePrintingMounting] =
    useState(false);
  const [showPrintingMountingOnly, setShowPrintingMountingOnly] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const pdfPreviewRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [eInvoiceInfo, setEinvoiceInfo] = useState({});

  // Add new states for email modal
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [bodyOfEmail, setBodyOfEmail] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [pdfS3Url, setPdfS3Url] = useState("");
  const [annexureExcelS3Url, setAnnexureExcelS3Url] = useState("");
  const [uploadError, setUploadError] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");

  const bdfCreatorId = invoiceData?.bdfCreatorId;
  const bdfCreatorName = invoiceData?.bdfCreatorName;

  const userSelector = (state) => state.user;
  const user = useSelector(userSelector);

  // Email Editor configuration
  const modules = {
    toolbar: [
      [
        { bold: true },
        { italic: true },
        { underline: true },
        { link: true },
        { emoji: true },
      ],
    ],
  };

  const formats = ["bold", "italic", "underline", "link", "emoji"];

  // Set default email body when PDF S3 URL is available
  useEffect(() => {
    if (pdfS3Url) {
      const partyName =
        invoiceData?.partyName ||
        invoiceData?.aliasPartyName ||
        invoiceData?.splitParentCompanyName ||
        invoiceData?.parentCompanyName ||
        "Client";

      const billNumber = invoiceData?.billNumber || "";

      setEmailSubject(`Invoice ${billNumber} for ${partyName}`);

      // Create email body with both PDF and Excel links if available
      let emailBody = `<p>Dear ${bdfCreatorName},</p>
      <br/>
      <p>Please find below the link(s) to download the Invoice No. ${billNumber}:</p>
      <br/>
      <p><a href="${getViewableFileUrl(pdfS3Url)}" target="_blank">Download Invoice</a></p>`;

      // Add Excel link if annexure is enabled and Excel URL is available
      if (asPerAnnexure && annexureExcelS3Url) {
        emailBody += `<br/>
        <p><a href="${getViewableFileUrl(annexureExcelS3Url)}" target="_blank">Download Annexure Excel</a></p>`;
      }

      emailBody += `<br/>
      <br/>
      <p>Best regards,</p>
      <p>${user?.name}</p>
      <br/>`;

      setBodyOfEmail(emailBody);
    }
  }, [
    pdfS3Url,
    annexureExcelS3Url,
    asPerAnnexure,
    invoiceData,
    bdfCreatorName,
    user?.name,
  ]);

  // console.log("Invoice data: ", invoiceData);
  useEffect(() => {
    // console.log("This is the invoice data coming from ut", invoiceData);
    const generateQRCode = async () => {
      if (eInvoice && invoiceData?.SignedQRCode) {
        setEinvoiceInfo({
          irn: invoiceData?.IRN || "",
          ackNo: invoiceData?.AckNo || "",
          ackDate: invoiceData?.AckDate || "",
        });
        const dataUrl = await QRCode.toDataURL(invoiceData.SignedQRCode);
        // console.log("This is the dataaaa url", dataUrl);
        setQrCodeDataUrl(dataUrl);
      }
    };
    generateQRCode();
  }, [invoiceData.SignedQRCode]);
  const toWords = new ToWords({
    localeCode: "en-IN",
    converterOptions: {
      currency: false,
      ordinal: false,
    },
  });

  const generatePDF = () => {
    const doc = new jsPDF("p", "mm", "a4");

    const drawCancelledWatermark = (doc) => {
      const pageCount = doc.getNumberOfPages();
      const watermarkText = "CANCELLED";

      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        doc.saveGraphicsState();

        doc.setTextColor(255, 80, 80); // Light Red
        doc.setFont("helvetica", "bold");
        doc.setFontSize(80);

        // 🔧 Add offset here:
        const offsetX = 25; // shift right
        const offsetY = 40; // shift down

        doc.text(
          watermarkText,
          pageWidth / 2 + offsetX,
          pageHeight / 2 + offsetY,
          {
            angle: 45,
            align: "center",
            baseline: "middle",
          }
        );

        doc.restoreGraphicsState();
      }
    };

    // Shift the complete invoice layout upwards to reduce unused top whitespace.
    // Negative value moves content up.
    const layoutShiftY = -3.5;
    let startY = 70 + layoutShiftY;
    const columnWidths = [10, 97, 16, 21, 21, 35];
    const rowHeight = 8;
    // Left gutter so printed pages can be hole-punched without clipping content.
    const punchMarginMm = 2;
    const leftX = 10 + punchMarginMm;
    const rightX = 200 + punchMarginMm;
    let headers = [];
    const gstNumberRaw =
      invoiceData?.partyGstNumber ||
      invoiceData?.splitGSTNumber ||
      invoiceData?.gstNumber ||
      "";
    const gstNumber =
      typeof gstNumberRaw === "string" ? gstNumberRaw.trim() : "";
    const hasValidGstNumber =
      gstNumber !== "" && gstNumber !== "NA" && gstNumber.length >= 2;
    doc.setFont("helvetica", "bold").setFontSize(10);

    const addHeader = () => {
      // Header Section
      doc.setFont("helvetica", "bold").setFontSize(10);
      doc.text("TAX INVOICE", leftX, 25 + layoutShiftY, { maxWidth: 100 });

      doc.setFontSize(8);
      doc.text("(Original for Buyer)", 35 + punchMarginMm, 25 + layoutShiftY);

      doc.setFontSize(14).setFont("helvetica", "bold");
      if (showHeading) {
        doc.text("Yellow Spot OOH", leftX, 30 + layoutShiftY, { maxWidth: 100 });
      }
      doc.setFont("helvetica", "normal").setFontSize(10);

      const headerAddress = `12th Block, 4th Floor, Magnet Corporate Park, Opp. Grand Cambay, Thaltej, Ahmedabad-380054.`;
      const headerAddressLines = doc.splitTextToSize(headerAddress, 100);
      doc.text(headerAddressLines, leftX, 35 + layoutShiftY);

      const headerAddressHeight = headerAddressLines.length * 5;

      let contentStartY = 30 + layoutShiftY + headerAddressHeight + 5;

      doc.setFont("helvetica", "bold");
      doc.text("Phone No. : 9033001770", leftX, contentStartY);
      contentStartY += 5;
      doc.text(
        "GST No. : 24AADFY0077E1ZE, PAN No. : AADFY0077E",
        leftX,
        contentStartY
      );
      contentStartY += 5;
      doc.text("MSME No. : UDYAM-GJ-01-0215162", leftX, contentStartY);
      doc.setFont("helvetica", "normal");

      if (showLogo) {
        doc.addImage("/images/pptSlide11.png", "PNG", 165 + punchMarginMm, 20 + layoutShiftY, 35, 35);
      }
      if (invoiceData.SignedQRCode) {
        doc.addImage(qrCodeDataUrl, "PNG", 125 + punchMarginMm, 20 + layoutShiftY, 36, 36);
      }

      if (invoiceData.SignedQRCode) {
        // New coordinates for the rectangle
        const newRectX = leftX;
        const newRectY = contentStartY + 4; // Keep it fixed below MSME on every page
        const newRectWidth = 190; // Width of the rectangle
        const newRectHeight = 10; // Decreased height of the rectangle

        // Draw the rectangle (2 vertical and 2 horizontal lines)
        doc.line(newRectX, newRectY, newRectX, newRectY + newRectHeight); // Left vertical line
        doc.line(
          newRectX + newRectWidth,
          newRectY,
          newRectX + newRectWidth,
          newRectY + newRectHeight
        ); // Right vertical line

        doc.line(newRectX, newRectY, newRectX + newRectWidth, newRectY); // Top horizontal line
        doc.line(
          newRectX,
          newRectY + newRectHeight,
          newRectX + newRectWidth,
          newRectY + newRectHeight
        ); // Bottom horizontal line

        // Add the text inside the rectangle (IRN, Ack.No., Ack.Date)
        doc.setFont("helvetica", "bold").setFontSize(8);

        // Place the IRN
        doc.text(
          `IRN: ${invoiceData?.IRN || "N/A"}`,
          newRectX + 5,
          newRectY + 4
        );

        // Place the Ack.No.
        doc.text(
          `Ack.No: ${invoiceData?.AckNo || "N/A"}`,
          newRectX + 5,
          newRectY + 8
        );

        // Place the Ack.Date
        doc.text(
          `Ack.Date: ${invoiceData?.AckDate || "N/A"}`,
          newRectX + 70,
          newRectY + 8
        );

        doc.setFont("helvetica", "normal").setFontSize(10);
      }

      let addressLeftY = 70 + layoutShiftY;

      const {
        gstDisplay,
        stateName,
        stateCode,
        panNumber: panNumberForDisplay,
        placeOfSupply,
      } = resolvePdfPartyTaxFields(invoiceData, gstNumber, hasValidGstNumber);

      const partyAddressThreeLines = buildPartyAddressThreeLinesForPdf(
        doc,
        invoiceData,
        85
      );

      const partyName =
        invoiceData?.partyName ||
        invoiceData?.aliasPartyName ||
        invoiceData?.splitParentCompanyName ||
        invoiceData?.parentCompanyName ||
        "N/A";

      const addressLeftText = [
        "To,",
        partyName,
        partyAddressThreeLines[0],
        partyAddressThreeLines[1],
        partyAddressThreeLines[2],
        `State : ${stateName}, Code : ${stateCode}`,
        `GST No. : ${gstDisplay}`,
        `PAN No. : ${panNumberForDisplay}`,
      ];

      // Slightly smaller wrap width so long addresses break earlier and don't touch the box border.
      const wrappedAddressLeftText = doc.splitTextToSize(addressLeftText, 85);
      const partyNameLineCount = doc.splitTextToSize(partyName, 85).length;
      const partyNameStartIndex = 1;
      const partyNameEndIndex = partyNameStartIndex + partyNameLineCount - 1;
      const lineHeight = 5;
      const addressLeftHeight = wrappedAddressLeftText.length * lineHeight + 2;

      wrappedAddressLeftText.forEach((line, index) => {
        if (
          (index >= partyNameStartIndex && index <= partyNameEndIndex) ||
          index === wrappedAddressLeftText.length - 1 ||
          index === wrappedAddressLeftText.length - 2
        ) {
          doc.setFont("helvetica", "bold");
        } else {
          doc.setFont("helvetica", "normal");
        }

        const currentYPosition = addressLeftY + index * lineHeight;

        doc.text(line, 15 + punchMarginMm, currentYPosition + 5);
      });

      const addressRightY = addressLeftY;
      const addressRightText = [
        `Bill No. : ${invoiceData?.billNumber ? invoiceData?.billNumber : "NA"
        }                Date : ${moment(invoiceData?.createdAt).format(
          "DD-MM-YYYY"
        )}`,
        `Ref. No. : ${invoiceData?.refNo ? invoiceData?.refNo : ""}`,
        `Ref. Date : ${invoiceData?.refDate
          ? moment(invoiceData?.refDate).format("DD-MM-YYYY")
          : ""
        }`,
        `Brand Name : ${invoiceData?.brandName?.toUpperCase() || "NA"}`,
        `Campaign Name : ${invoiceData?.campaignName?.toUpperCase() || "NA"}`,
        `Place of Supply : ${placeOfSupply}`,
        `Reverse Charge : N`,
      ];
      // Wrap using the same font as drawing (bold); otherwise widths are wrong and long
      // "Brand Name :" / "Campaign Name :" lines can overflow. Applies to all right-box lines.
      doc.setFont("helvetica", "bold").setFontSize(10);
      const rightBoxMaxWidth = 90; // rect width is 95mm, keep some padding
      const wrappedAddressRightText = addressRightText.flatMap((line) =>
        doc.splitTextToSize(String(line ?? ""), rightBoxMaxWidth)
      );
      const addressRightHeight = wrappedAddressRightText.length * 5 + 10;

      const maxHeight = Math.max(addressLeftHeight, addressRightHeight);

      doc.rect(leftX, addressLeftY, 95, maxHeight);

      doc.rect(105 + punchMarginMm, addressRightY, 95, maxHeight);

      wrappedAddressRightText.forEach((line, index) => {
        doc.text(line, 110 + punchMarginMm, addressRightY + 5 + index * 5);
      });
      doc.setFont("helvetica", "normal");

      startY = addressLeftY + maxHeight;

      doc.rect(leftX, startY, 190, rowHeight);

      // Must match divider2X below: left edge of Amount column (between "To Date" and "Amount").
      const toDateAmountDividerX =
        leftX + columnWidths.slice(0, -1).reduce((a, b) => a + b, 0);
      doc.line(leftX, startY, leftX, startY + rowHeight);
      doc.line(leftX + columnWidths[0], startY, leftX + columnWidths[0], startY + rowHeight);
      doc.line(
        toDateAmountDividerX,
        startY,
        toDateAmountDividerX,
        startY + rowHeight
      );
      doc.line(rightX, startY, rightX, startY + rowHeight);

      headers = ["Sr", "Media/Location", "Size"];
      if (showPeriod) {
        headers.push("From Date", "To Date");
      } else {
        headers.push("", "");
      }

      headers.push("Amount");

      doc.setFont("helvetica", "bold");

      // Display headers maintaining their positions
      headers.forEach((header, i) => {
        // Calculate position based on column widths
        const position =
          leftX + 2 + columnWidths.slice(0, i).reduce((a, b) => a + b, 0);

        doc.text(header, position, startY + 5);
      });

      doc.setFont("helvetica", "normal");

      startY += rowHeight;
    };

    addHeader();

    const totalSpecialRate =
      (invoiceData?.billedInvs?.reduce(
        (sum, inventory) => sum + (inventory?.amount || 0),
        0
      ) || 0) +
      (invoiceData?.suffixInventories?.reduce(
        (sum, inventory) => sum + (parseFloat(inventory?.specialRate) || 0),
        0
      ) || 0);

    // Sum up the printingCharge and mountingCharge from the inventories array
    const totalProductionCharges = {
      printingCharge: Number(invoiceData?.printingCharge) || 0,
      mountingCharge: Number(invoiceData?.mountingCharge) || 0,
    };

    const totalProductionCost =
      (Number(totalProductionCharges?.printingCharge) || 0) +
      (Number(totalProductionCharges?.mountingCharge) || 0);

    const effectiveSpecialRate = showPrintingMountingOnly ? 0 : totalSpecialRate;

    const total = effectiveSpecialRate + totalProductionCost;
    // const total = totalSpecialRate;

    const totalFormatted = (Number(total) || 0).toFixed(2);

    const totalFormattedDisplay = new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(total);

    const remarkForPdf =
      invoiceData?.invoiceRemark != null
        ? String(invoiceData.invoiceRemark).trim()
        : DEFAULT_INVOICE_REMARK;
    const subRemarkTrimmed =
      (invoiceData?.invoiceSubRemark &&
        String(invoiceData.invoiceSubRemark).trim()) ||
      "";

    const rows = [];

    rows.push(["", remarkForPdf, "", "", "", ""]);

    if (subRemarkTrimmed) {
      rows.push(["", subRemarkTrimmed, "", "", "", ""]);
    }

    // Add this condition to check if asPerAnnexure is true
    if (asPerAnnexure) {
      // If "As per annexure" is checked, add just one row with "As Per Annexure" text
      // and the total amount
      rows.push([
        "",
        "As Per Annexure",
        "",
        "",
        "",
        totalFormattedDisplay, // Show the total amount directly
      ]);
    } else {
      // Original code for when "As per annexure" is not checked
      if (showHoardingWisePrintingMounting) {
        // Each inventory shown twice: once for Printing Charge, once for Mounting Charge (per hoarding)
        const formatter = new Intl.NumberFormat("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        const billedInvListAll = invoiceData?.billedInvs || [];
        const billedInvList = showZeroAmountInventories
          ? billedInvListAll
          : billedInvListAll.filter((inv) => Number(inv?.amount) !== 0);
        const invoiceLevelPrinting =
          Number(totalProductionCharges?.printingCharge) || 0;
        const invoiceLevelMounting =
          Number(totalProductionCharges?.mountingCharge) || 0;
        let sumPerLinePrinting = 0;
        let sumPerLineMounting = 0;
        billedInvList.forEach((billed) => {
          const m = invoiceData?.inventories?.find(
            (inv) => inv?._id === billed?._id
          );
          sumPerLinePrinting +=
            Number(m?.printingCharge) || Number(billed?.printingCharge) || 0;
          sumPerLineMounting +=
            Number(m?.mountingCharge) || Number(billed?.mountingCharge) || 0;
        });
        const billedAmountTotal = billedInvList.reduce(
          (s, inv) => s + (Number(inv?.amount) || 0),
          0
        );
        const useInvoiceLevelSplit =
          (invoiceLevelPrinting > 0 || invoiceLevelMounting > 0) &&
          sumPerLinePrinting === 0 &&
          sumPerLineMounting === 0;

        let srNo = 0;
        billedInvList.forEach((inventory) => {
          const matchedInv = invoiceData?.inventories?.find(
            (inv) => inv?._id === inventory?._id
          );
          let printingCharge =
            Number(matchedInv?.printingCharge) ||
            Number(inventory?.printingCharge) ||
            0;
          let mountingCharge =
            Number(matchedInv?.mountingCharge) ||
            Number(inventory?.mountingCharge) ||
            0;
          if (useInvoiceLevelSplit) {
            const weight =
              billedAmountTotal > 0
                ? (Number(inventory?.amount) || 0) / billedAmountTotal
                : billedInvList.length > 0
                  ? 1 / billedInvList.length
                  : 1;
            printingCharge = invoiceLevelPrinting * weight;
            mountingCharge = invoiceLevelMounting * weight;
          }
          const locationName = inventory?.locationName || "NA";
          const sizeStr = `${inventory?.width} x ${inventory?.height}` || "NA";
          const fromDate = moment(inventory.startDate).format("DD-MM-YYYY");
          const toDate = moment(inventory.endDate).format("DD-MM-YYYY");

          srNo += 1;
          rows.push([
            `${srNo}`,
            `${locationName} - Printing Charge`,
            sizeStr,
            fromDate,
            toDate,
            showItemAmount && srNo === 1
              ? totalFormattedDisplay
              : showItemAmount
                ? ""
                : formatter.format(printingCharge),
          ]);
          srNo += 1;
          rows.push([
            `${srNo}`,
            `${locationName} - Mounting Charge`,
            sizeStr,
            fromDate,
            toDate,
            showItemAmount ? "" : formatter.format(mountingCharge),
          ]);
        });
      } else {
        if (!showPrintingMountingOnly) {
          const billedInvsAll = invoiceData?.billedInvs || [];
          const billedInvsForRows = showZeroAmountInventories
            ? billedInvsAll
            : billedInvsAll.filter((inv) => Number(inv?.amount) !== 0);
          rows.push(
            ...(billedInvsForRows?.map((inventory, index) => [
              `${index + 1}`,
              inventory?.locationName || "NA",
              `${inventory?.width} x ${inventory?.height}` || "NA",
              moment(inventory.startDate).format("DD-MM-YYYY"),
              moment(inventory.endDate).format("DD-MM-YYYY"),
              // Only show amount for the first inventory when showItemAmount is true
              showItemAmount && index === 0
                ? totalFormattedDisplay
                : showItemAmount
                  ? ""
                  : new Intl.NumberFormat("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(inventory?.amount || 0),
            ]) || [])
          );
        }
      }

      // Add suffixInventories rows if they exist
      const billedInvsAll = invoiceData?.billedInvs || [];
      const billedInvsLength = showZeroAmountInventories
        ? billedInvsAll.length
        : billedInvsAll.filter((inv) => Number(inv?.amount) !== 0).length;
      const effectiveBilledInvsRowCount = showHoardingWisePrintingMounting
        ? billedInvsLength * 2
        : billedInvsLength;

      let suffixInventoriesCountForRows = 0;
      if (
        !showPrintingMountingOnly &&
        invoiceData?.suffixInventories &&
        invoiceData.suffixInventories.length > 0
      ) {
        const suffixAll = invoiceData.suffixInventories || [];
        const suffixForRows = showZeroAmountInventories
          ? suffixAll
          : suffixAll.filter((s) => Number(parseFloat(s?.specialRate)) !== 0);
        suffixInventoriesCountForRows = suffixForRows.length;
        rows.push(
          ...(suffixForRows?.map((suffix, index) => [
            `${effectiveBilledInvsRowCount + index + 1}`, // Serial number continues from billedInvs
            suffix?.suffixInfo || "NA",
            "", // Empty size
            moment(suffix.startDate).format("DD-MM-YYYY"),
            moment(suffix.endDate).format("DD-MM-YYYY"),
            // Only show amount for the first item when showItemAmount is true
            // If billedInvs is empty and this is the first suffixInventory item, show the total
            showItemAmount && effectiveBilledInvsRowCount === 0 && index === 0
              ? totalFormattedDisplay
              : showItemAmount
                ? ""
                : new Intl.NumberFormat("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(parseFloat(suffix?.specialRate) || 0),
          ]) || [])
        );
      }

      // Calculate the next serial number for production charges
      const nextSerialNumber = showPrintingMountingOnly
        ? 1
        : effectiveBilledInvsRowCount +
          suffixInventoriesCountForRows +
          1;

      // When hoarding-wise printing/mounting is on, production charges are already per-row; skip aggregate rows
      // Handle production charges based on the showSeparateProductionCharges option
      const bdfDateStr = formatDateForPdfDdMmYyyy(
        invoiceData?.bdfDate,
        invoiceData?.createdAt
      );
      // Same rule as PdfFormatJsx: BDF date in printing/mounting rows only for Sub BDF invoices.
      const isFromSubInvoice = Boolean(invoiceData?.isFromSub);
      const productionFromDate =
        showPeriod && isFromSubInvoice ? bdfDateStr : "";
      const productionToDate =
        showPeriod && isFromSubInvoice ? bdfDateStr : "";

      if (!showHoardingWisePrintingMounting && showSeparateProductionCharges) {
        // Show printing and mounting charges separately
        if (Number(totalProductionCharges?.printingCharge) > 0) {
          rows.push([
            `${nextSerialNumber}`,
            "Printing Charges",
            "",
            productionFromDate,
            productionToDate,
            // Only show the amount when showItemAmount is false
            showItemAmount
              ? ""
              : new Intl.NumberFormat("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(Number(totalProductionCharges?.printingCharge) || 0),
          ]);
        }

        if (Number(totalProductionCharges?.mountingCharge) > 0) {
          rows.push([
            `${nextSerialNumber +
            (Number(totalProductionCharges?.printingCharge) > 0 ? 1 : 0)
            }`,
            "Mounting Charges",
            "",
            productionFromDate,
            productionToDate,
            // Only show the amount when showItemAmount is false
            showItemAmount
              ? ""
              : new Intl.NumberFormat("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(Number(totalProductionCharges?.mountingCharge) || 0),
          ]);
        }
      } else if (!showHoardingWisePrintingMounting) {
        // Show combined printing & mounting charges only if the total is greater than 0
        if (Number(totalProductionCost) > 0) {
          rows.push([
            `${nextSerialNumber}`,
            "Printing & Mounting Charges",
            "",
            productionFromDate,
            productionToDate,
            // Only show the amount when showItemAmount is false
            showItemAmount
              ? ""
              : new Intl.NumberFormat("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(Number(totalProductionCost) || 0),
          ]);
        }
      }
    }

    // rows.push(
    //   ...Array(10).fill([
    //     "",
    //     "test",
    //     "test test test test test test test test test test test test test",
    //     "9",
    //     "9",
    //     "9",
    //     "9",
    //     "9",
    //   ])
    // );

    if (rows.length <= 4) {
      rows.push(...Array(2).fill(["", "", "", "", "", ""]));
    }

    const totalAmount = parseFloat(totalFormatted);

    const sgstAmount = (totalAmount * (invoiceData?.SGST || 0)) / 100;
    const cgstAmount = (totalAmount * (invoiceData?.CGST || 0)) / 100;
    const igstAmount = (totalAmount * (invoiceData?.IGST || 0)) / 100;
    // const agencyCommAmount =
    //   (totalAmount * (invoiceData?.agencyCommission || 0)) / 100;
    // const municipalTaxAmount =
    //   (totalAmount * (invoiceData?.municipalTax || 0)) / 100;
    // const otherTaxAmount = (totalAmount * (invoiceData?.otherTaxes || 0)) / 100;
    // const roundTaxAmount = (totalAmount * (invoiceData?.round || 0)) / 100;

    // With these lines that use the Indian number format:
    const formatter = new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    // Note: Keep the original values for calculation purposes
    const sgstAmountValue = parseFloat((Number(sgstAmount) || 0).toFixed(2));
    const cgstAmountValue = parseFloat((Number(cgstAmount) || 0).toFixed(2));
    const igstAmountValue = parseFloat((Number(igstAmount) || 0).toFixed(2));
    // const roundAmountValue = (Number(roundTaxAmount) || 0).toFixed(2);

    // Calculate the sum before rounding
    const sumBeforeRounding =
      totalAmount + sgstAmountValue + cgstAmountValue + igstAmountValue;

    // Get the desired grand total from invoiceData, or use the sum if not specified
    // Apply consistent rounding in both cases
    const desiredGrandTotal = Math.round(sumBeforeRounding); // Round the calculated sum

    // Calculate the rounding adjustment
    const roundTaxAmount = desiredGrandTotal - sumBeforeRounding;
    const roundAmountValue = parseFloat(
      (Number(roundTaxAmount) || 0).toFixed(2)
    );

    // Format all values for display
    const sgstAmountFormatted = formatter.format(sgstAmountValue);
    const cgstAmountFormatted = formatter.format(cgstAmountValue);
    const igstAmountFormatted = formatter.format(igstAmountValue);
    const roundAmountFormatted = formatter.format(roundAmountValue);

    doc.setFontSize(9);

    let newSectionY;
    let newSectionHeight;

    const rowPadding = 2; // existing code uses "+ 2" when drawing row borders
    const tableStartY = startY;

    // We want the inventories table block to occupy the same vertical space on every page.
    // So we calculate the max row height that can fit (with the same footer/sum layout),
    // then render each inventory row using that fixed height.
    function measureFooterHeight() {
      const termsText = [
        "1. Cheque & NEFT/RTGS Should be in favour of 'Yellow Spot OOH'.",
        "2. Please do not Deduct TDS On GST.",
        "3. Payment Immediately after issuing this bill.",
        "4. Interest at 18% per annum will be charged on overdue accounts from the date of this bill.",
        "5. Subject to Ahmedabad Jurisdiction.",
        "6. Once ordered should not be cancelled.",
      ];

      const termsMaxWidth = 110;
      const lineSpacing = 2.3;
      const pointSpacing = 0.3;

      // In `addFooter()`, the "Terms & Conditions:" title is drawn at finalSectionY+5,
      // and then `currentY` starts from finalSectionY+10.
      // We measure everything after `finalSectionY` with a relative origin of 0.
      let currentY = 10;

      doc.setFont("helvetica", "normal").setFontSize(7.5);
      termsText.forEach((text, index) => {
        const wrappedText = doc.splitTextToSize(text, termsMaxWidth);
        currentY += wrappedText.length * lineSpacing;
        if (index < termsText.length - 1) currentY += pointSpacing;
      });

      // In `addFooter()` we place bank text immediately after terms (no extra heading gap).
      const bankDetailsY = currentY;
      // Match addFooter: tight pad below last ink (bank line / stamp).
      const stampRelBottom = 27.2;
      return Math.max(bankDetailsY + 8.2, stampRelBottom);
    }

    const footerHeight = measureFooterHeight();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Reserve summary (Total + Rupees) height on every page to keep layout identical.
    const summaryReservedHeight = 28 + 10;

    // Fixed inventories/table block height: same end-Y across all pages.
    const safetyMarginMm = 23.5; // keep a bit more bottom gap below inventories
    const inventorySectionFixedHeight = Math.max(
      rowHeight,
      pageHeight -
      footerHeight -
      summaryReservedHeight -
      tableStartY -
      safetyMarginMm
    );

    const divider1X = leftX + columnWidths[0];
    // Divider between "To Date" and "Amount"
    const divider2X =
      leftX + columnWidths.slice(0, -1).reduce((a, b) => a + b, 0);

    function drawInventoryBorders(fromY, toY) {
      doc.line(leftX, fromY, leftX, toY);
      doc.line(divider1X, fromY, divider1X, toY);
      doc.line(divider2X, fromY, divider2X, toY);
      doc.line(rightX, fromY, rightX, toY);
    }

    // Reset font size after measuring footer.
    doc.setFont("helvetica", "normal").setFontSize(9);

    let pageTableStartY = startY;
    let sectionEndY = pageTableStartY + inventorySectionFixedHeight;

    let rowIndex = 0;

    const isWideDescriptionRow = (idx) =>
      idx === 0 ||
      (subRemarkTrimmed.length > 0 &&
        idx === 1 &&
        rows[1] &&
        String(rows[1][1] ?? "") === subRemarkTrimmed);

    // Draw inventory rows, then reserve the same fixed height to summary/footer on each page.
    while (rowIndex < rows.length) {
      const row = rows[rowIndex];
      const globalRowIndex = rowIndex;

      // 1) Measure the row height based on how the text wraps.
      let rowTotalHeight = rowHeight;
      row.forEach((text, index) => {
        const safeText = String(text ?? "");

        if (isWideDescriptionRow(globalRowIndex)) {
          const staticTextWidth = 150;
          const wrappedText =
            doc.getTextWidth(safeText) > staticTextWidth
              ? doc.splitTextToSize(safeText, staticTextWidth)
              : [safeText];
          rowTotalHeight = Math.max(rowTotalHeight, wrappedText.length * 5);
          return;
        }

        if (!showPeriod && (index === 3 || index === 4)) return;

        const wrappedText = doc.splitTextToSize(
          safeText,
          columnWidths[index] - 3
        );
        rowTotalHeight = Math.max(rowTotalHeight, wrappedText.length * 5);
      });

      const remainingSpace = sectionEndY - startY;

      // 2) If it doesn't fit in remaining height, finish the current page and try again.
      if (rowTotalHeight + rowPadding > remainingSpace) {
        // Push to next page only if we already placed something on this page.
        if (startY > pageTableStartY) {
          drawInventoryBorders(pageTableStartY, sectionEndY);
          doc.setFontSize(10);
          doc.line(leftX, sectionEndY, rightX, sectionEndY);
          startY = sectionEndY;

          addSummarySection(false);
          addFooter();

          doc.addPage();
          addHeader();
          startY = startY || 70 + layoutShiftY;

          pageTableStartY = startY;
          sectionEndY = pageTableStartY + inventorySectionFixedHeight;
          continue; // retry same row on the next page
        }

        // If the row doesn't fit even on an empty inventories block,
        // truncate it to the maximum lines that can fit in this page.
        const maxRowHeight = Math.max(rowHeight, remainingSpace - rowPadding);
        const maxLinesAllowed = Math.max(1, Math.floor(maxRowHeight / 5));

        const cellLines = row.map((text, index) => {
          const safeText = String(text ?? "");

          if (isWideDescriptionRow(globalRowIndex)) {
            const staticTextWidth = 150;
            const wrappedText =
              doc.getTextWidth(safeText) > staticTextWidth
                ? doc.splitTextToSize(safeText, staticTextWidth)
                : [safeText];
            return wrappedText.length > maxLinesAllowed
              ? wrappedText.slice(0, maxLinesAllowed)
              : wrappedText;
          }

          if (!showPeriod && (index === 3 || index === 4)) return null;

          const wrappedText = doc.splitTextToSize(
            safeText,
            columnWidths[index] - 3
          );

          return wrappedText.length > maxLinesAllowed
            ? wrappedText.slice(0, maxLinesAllowed)
            : wrappedText;
        });

        // Determine the actual row height using the widest (max-lines) cell.
        let actualRowTotalHeight = rowHeight;
        cellLines.forEach((lines) => {
          if (!lines) return;
          actualRowTotalHeight = Math.max(
            actualRowTotalHeight,
            lines.length * 5
          );
        });

        let currentX = leftX;
        cellLines.forEach((lines, index) => {
          if (!lines) {
            currentX += columnWidths[index];
            return;
          }

          const textY =
            startY +
            actualRowTotalHeight / 2 -
            (lines.length * 5) / 2 +
            3;

          lines.forEach((line, lineIndex) => {
            if (index === cellLines.length - 1) {
              doc.text(
                line,
                rightX - 3,
                textY + lineIndex * 5 + 2,
                { align: "right", maxWidth: rightX - divider2X - 6 }
              );
            } else {
              doc.text(line, currentX + 2, textY + lineIndex * 5 + 2);
            }
          });

          currentX += columnWidths[index];
        });

        startY += actualRowTotalHeight + rowPadding;
        rowIndex++;
        continue;
      }

      // 3) Draw the row (no truncation needed).
      let currentX = leftX;

      row.forEach((text, index) => {
        const safeText = String(text ?? "");

        if (isWideDescriptionRow(globalRowIndex)) {
          const staticTextWidth = 150;
          const wrappedText =
            doc.getTextWidth(safeText) > staticTextWidth
              ? doc.splitTextToSize(safeText, staticTextWidth)
              : [safeText];

          const textY =
            startY + rowTotalHeight / 2 - (wrappedText.length * 5) / 2 + 3;

          wrappedText.forEach((line, lineIndex) => {
            doc.text(line, currentX + 2, textY + lineIndex * 5 + 2);
          });

          currentX += columnWidths[index];
          return;
        }

        if (!showPeriod && (index === 3 || index === 4)) {
          currentX += columnWidths[index];
          return;
        }

        const wrappedText = doc.splitTextToSize(
          safeText,
          columnWidths[index] - 3
        );

        const textY =
          startY + rowTotalHeight / 2 - (wrappedText.length * 5) / 2 + 3;

        wrappedText.forEach((line, lineIndex) => {
          if (index === row.length - 1) {
            doc.text(
              line,
              rightX - 3,
              textY + lineIndex * 5 + 2,
              { align: "right", maxWidth: rightX - divider2X - 6 }
            );
          } else {
            doc.text(line, currentX + 2, textY + lineIndex * 5 + 2);
          }
        });

        currentX += columnWidths[index];
      });

      startY += rowTotalHeight + rowPadding;
      rowIndex++;
    }

    // Finalize last page inventories block.
    drawInventoryBorders(pageTableStartY, sectionEndY);
    startY = sectionEndY;

    doc.setFontSize(10);

    doc.line(leftX, startY, rightX, startY);

    addSummarySection(true);

    function addSummarySection(isLastPage = false) {
      const firstSectionY = startY;
      const firstSectionHeight = 28; // Always reserve to keep page layout fixed.
      doc.rect(leftX, firstSectionY, 95, firstSectionHeight);

      if (isLastPage) {
        doc.setFont("helvetica", "bold");
        // doc.text("PAN No. : AADFY0077E", 15, firstSectionY + 10);
        doc.text("GST Category :  Advertising Agency", 15 + punchMarginMm, firstSectionY + 10);
        doc.text(`HSN/SAC : ${invoiceData?.HSN}`, 15 + punchMarginMm, firstSectionY + 15);
        doc.setFont("helvetica", "normal");
      }

      const secondSectionY = firstSectionY;

      const maxHeight2 = firstSectionHeight;

      const secondSectionX = 105 + punchMarginMm;
      const thirdSectionX = 175 + punchMarginMm;
      doc.rect(secondSectionX, secondSectionY, 70, maxHeight2);
      doc.rect(thirdSectionX, secondSectionY, 25, maxHeight2);

      if (isLastPage) {
        let currentY = secondSectionY + 5;
        doc.text("Total", secondSectionX + 5, currentY);
        doc.text(totalFormattedDisplay, thirdSectionX + 23, currentY, {
          align: "right",
        });
        currentY += 5;

        if (invoiceData?.SGST) {
          doc.text(
            `SGST : ${invoiceData?.SGST} %`,
            secondSectionX + 5,
            currentY
          );
          doc.text(sgstAmountFormatted, thirdSectionX + 23, currentY, {
            align: "right",
          });
          currentY += 5;
        }

        if (invoiceData?.CGST) {
          doc.text(
            `CGST : ${invoiceData?.CGST} %`,
            secondSectionX + 5,
            currentY
          );
          doc.text(cgstAmountFormatted, thirdSectionX + 23, currentY, {
            align: "right",
          });
          currentY += 5;
        }

        if (invoiceData?.IGST) {
          doc.text(
            `IGST : ${invoiceData?.IGST} %`,
            secondSectionX + 5,
            currentY
          );
          doc.text(igstAmountFormatted, thirdSectionX + 23, currentY, {
            align: "right",
          });
          currentY += 5;
        }

        if (roundAmountFormatted !== "0.00") {
          doc.text(`Round off`, secondSectionX + 5, currentY);
          doc.text(roundAmountFormatted, thirdSectionX + 23, currentY, {
            align: "right",
          });
        }
      }

      // if (isLastPage) {
      //   doc.text(totalFormattedDisplay, thirdSectionX + 2, secondSectionY + 5);
      //   doc.text(sgstAmountFormatted, thirdSectionX + 2, secondSectionY + 10);
      //   doc.text(cgstAmountFormatted, thirdSectionX + 2, secondSectionY + 15);
      //   doc.text(igstAmountFormatted, thirdSectionX + 2, secondSectionY + 20);
      //   doc.text(roundAmountFormatted, thirdSectionX + 2, secondSectionY + 25);
      // }

      doc.line(
        secondSectionX,
        secondSectionY + 21.5,
        secondSectionX + 70,
        secondSectionY + 21.5
      );
      doc.line(
        thirdSectionX,
        secondSectionY + 21.5,
        thirdSectionX + 25,
        secondSectionY + 21.5
      );
      // else {
      //   doc.line(
      //     secondSectionX,
      //     secondSectionY + 22,
      //     secondSectionX + 70,
      //     secondSectionY + 22
      //   );
      //   doc.line(
      //     thirdSectionX,
      //     secondSectionY + 22,
      //     thirdSectionX + 25,
      //     secondSectionY + 22
      //   );
      // }

      // Always reserve the Rupees section, even on non-last pages.
      newSectionY = secondSectionY + maxHeight2;
      newSectionHeight = 10;
      doc.rect(leftX, newSectionY, 190, newSectionHeight);

      if (isLastPage) {
        const grandTotal = desiredGrandTotal;
        const formattedGrandTotal = formatter.format(grandTotal);

        doc.setFont("helvetica", "bold");
        doc.text("Grand Total", secondSectionX + 5, secondSectionY + 26);
        doc.text(
          formattedGrandTotal,
          thirdSectionX + 23,
          secondSectionY + 26,
          { align: "right" }
        );
        doc.setFont("helvetica", "normal");

        const grandTotalInWords = toWords.convert(grandTotal);
        const formattedGrandTotalInWords = grandTotalInWords
          .toLowerCase()
          .split(" ")
          ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        doc.setFont("helvetica", "bold");
        doc.text("Rupees :-", 15 + punchMarginMm, newSectionY + 7);
        doc.setFont("helvetica", "normal");
        doc.text(formattedGrandTotalInWords + " Only", 35 + punchMarginMm, newSectionY + 7);
      }

      // Keep bottom border consistent.
      doc.line(leftX, newSectionY + newSectionHeight, rightX, newSectionY + newSectionHeight);
    }

    function addFooter() {
      const finalSectionY = newSectionY + newSectionHeight;

      doc.setFont("helvetica", "bold").setFontSize(10);
      doc.text("Terms & Conditions:", 15 + punchMarginMm, finalSectionY + 5);

      const termsText = [
        "1. Cheque & NEFT/RTGS Should be in favour of 'Yellow Spot OOH'.",
        "2. Please do not Deduct TDS On GST.",
        "3. Payment Immediately after issuing this bill.",
        "4. Interest at 18% per annum will be charged on overdue accounts from the date of this bill.",
        "5. Subject to Ahmedabad Jurisdiction.",
        "6. Once ordered should not be cancelled.",
      ];

      const termsMaxWidth = 110;
      doc.setFont("helvetica", "normal").setFontSize(7.5);
      const lineSpacing = 2.3;
      const pointSpacing = 0.3;

      let currentY = finalSectionY + 10;
      termsText.forEach((text, index) => {
        const wrappedText = doc.splitTextToSize(text, termsMaxWidth);

        wrappedText.forEach((line, lineIndex) => {
          doc.text(line, 15 + punchMarginMm, currentY);
          currentY += lineSpacing;
        });

        if (index < termsText.length - 1) currentY += pointSpacing;
      });

      const bankDetailsY = currentY;

      // First line with bold keys and normal values (tight spacing vs terms + between bank lines)
      doc.setFontSize(8);
      doc.setFont("helvetica", "bold");
      doc.text("BANK:-", 27 + punchMarginMm, bankDetailsY + 2);
      doc.setFont("helvetica", "normal");
      doc.text("AXIS BANK LTD.,", 38 + punchMarginMm, bankDetailsY + 2);
      doc.setFont("helvetica", "bold");
      doc.text("BRANCH:-", 62 + punchMarginMm, bankDetailsY + 2);
      doc.setFont("helvetica", "normal");
      doc.text("THALTEJ-AHMEDABAD", 77 + punchMarginMm, bankDetailsY + 2);

      // Second line with bold keys and normal values
      doc.setFont("helvetica", "bold");
      doc.text("A/C NO.:-", 30 + punchMarginMm, bankDetailsY + 5.5);
      doc.setFont("helvetica", "normal");
      doc.text("922020030481942,", 44 + punchMarginMm, bankDetailsY + 5.5);
      doc.setFont("helvetica", "bold");
      doc.text("IFSC CODE:-", 70 + punchMarginMm, bankDetailsY + 5.5);
      doc.setFont("helvetica", "normal");
      doc.text("UTIB0002998", 88 + punchMarginMm, bankDetailsY + 5.5);

      // Bottom border: minimal gap under A/C line & stamp (smaller descender allowance + thin border pad).
      const footerBottomY = Math.max(
        bankDetailsY + 5.5 + 2.5 + 0.2,
        finalSectionY + 5 + 22 + 0.2
      );
      const leftSectionHeight = footerBottomY - finalSectionY;

      doc.rect(leftX, finalSectionY, 115, leftSectionHeight);

      doc.rect(125 + punchMarginMm, finalSectionY, 75, leftSectionHeight);
      doc
        .setFont("helvetica", "bold")
        .text("For, Yellow Spot OOH", 150 + punchMarginMm, finalSectionY + 4);
      doc
        .setFont("helvetica", "bold")
        .text("[Authorised Signatory]", 150 + punchMarginMm, bankDetailsY + 6);
      doc.addImage(
        "/images/yellowspotStamp.png",
        "PNG",
        155 + punchMarginMm,
        finalSectionY + 5.5,
        22,
        22
      );
    }

    addFooter();

    if (invoiceData?.isRejected) {
      drawCancelledWatermark(doc);
    }

    return doc;
  };

  const handleGeneratePDF = () => {
    const doc = generatePDF();

    // Get the party name (using alias if available, otherwise parent company name)
    const partyName =
      invoiceData?.partyName ||
      invoiceData?.aliasPartyName ||
      invoiceData?.splitParentCompanyName ||
      invoiceData?.parentCompanyName;

    // Get the bill number
    const billNumber = invoiceData?.billNumber;

    // Get the campaign name
    const campaignName = invoiceData?.campaignName;

    // Determine if it's an E-Invoice or regular Invoice
    const invoiceType = invoiceData?.SignedQRCode ? "E-Invoice" : "Invoice";

    // Add "Annexure" to the filename if asPerAnnexure is true
    const annexureText = asPerAnnexure ? "_Annexure" : "";

    // Create the filename with original values
    const filename = `${billNumber}_${partyName}_${campaignName}_${invoiceType}${annexureText}.pdf`;

    // Save the file with the new name
    doc.save(filename);
  };

  const handlePreview = () => {
    const doc = generatePDF();
    const pdfBlob = doc.output("blob");

    if (pdfBlob) {
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(pdfUrl);
      setIsPreviewOpen(true);
    } else {
      console.error("Failed to generate PDF Blob.");
    }
  };

  useEffect(() => {
    if (isPreviewOpen && pdfUrl && pdfPreviewRef.current) {
      pdfPreviewRef.current.src = pdfUrl;
    }
  }, [isPreviewOpen, pdfUrl]);

  const handleModalClose = () => {
    setAsPerAnnexure(false);
    setShowItemAmount(false);
    setShowSeparateProductionCharges(false);
    setShowHoardingWisePrintingMounting(false);
    setShowPrintingMountingOnly(false);
    onClose(); // Call the original onClose function
  };

  const handleGenerateAnnexureExcel = () => {
    generateAnnexureExcel(invoiceData);
  };

  const handleTextArea = (value) => {
    setBodyOfEmail(value);
  };

  const handleSendEmail = async () => {
    try {
      setIsUploading(true);
      setUploadError(null);

      // Generate the PDF
      const doc = generatePDF();
      const pdfBlob = doc.output("blob");

      // Get the party name and bill number for the filename
      const partyName =
        invoiceData?.partyName ||
        invoiceData?.aliasPartyName ||
        invoiceData?.splitParentCompanyName ||
        invoiceData?.parentCompanyName ||
        "Unknown";
      const billNumber = invoiceData?.billNumber || Date.now();
      const campaignName = invoiceData?.campaignName || "Unknown";
      const invoiceType = invoiceData?.SignedQRCode ? "E-Invoice" : "Invoice";
      const annexureText = asPerAnnexure ? "_Annexure" : "";

      // Create filename for the PDF
      const filename = `${billNumber}_${partyName}_${campaignName}_${invoiceType}${annexureText}.pdf`;

      // Get upload policy from API
      const requestBody = {
        fileName: filename,
        mime: "application/pdf",
      };

      const response = await fetch(`${apiBaseUrl}/chats/uploadPolicy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Token ${window.localStorage.getItem("token")}`,
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.json();

      if (result?.data?.url && result?.data?.fields) {
        const formData = new FormData();

        // Ensure Content-Type is set correctly for S3 policy
        const expectedContentType = "application/pdf";

        // Add all fields from the upload policy
        for (const [key, value] of Object.entries(result.data.fields)) {
          // Override Content-Type if present to ensure exact match
          if (key.toLowerCase() === "content-type") {
            formData.set(key, expectedContentType);
          } else {
            formData.set(key, value);
          }
        }

        // Ensure Content-Type is set even if not in fields
        if (!formData.has("Content-Type") && !formData.has("content-type")) {
          formData.set("Content-Type", expectedContentType);
        }

        // Add the actual PDF file
        formData.append("file", pdfBlob, filename);

        // Upload to S3
        const uploadResponse = await fetch(result.data.url, {
          method: "POST",
          body: formData,
        });

        if (uploadResponse.ok) {
          // Construct the final URL
          const finalUrl = convertS3ToCloudFront(
            `${result.data.url}/${result.filePath}`,
          );
          setPdfS3Url(finalUrl);

          // If asPerAnnexure is true, also upload Excel file
          if (asPerAnnexure) {
            try {
              const excelS3Url = await generateAnnexureExcel(
                invoiceData,
                true,
                apiBaseUrl
              );
              if (excelS3Url) {
                setAnnexureExcelS3Url(excelS3Url);
              }
            } catch (excelError) {
              console.warn("Excel upload failed:", excelError);
              // Continue with PDF only
            }
          }

          // Now open the email modal
          setIsEmailModalOpen(true);
          // notification.success({ message: "PDF uploaded successfully!" });
        } else {
          throw new Error("PDF upload failed");
        }
      } else {
        throw new Error("Missing required data in upload response");
      }
    } catch (error) {
      console.error("Error uploading PDF:", error);
      setUploadError(error.message);
      // notification.error({ message: `Error: ${error.message}` });
      // Still open the email modal, but without the PDF link
      setIsEmailModalOpen(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleEmailModalClose = () => {
    setIsEmailModalOpen(false);
    setPdfS3Url("");
    setAnnexureExcelS3Url("");
    setBodyOfEmail("");
  };

  // New function to handle sending the email
  const handleSendEmailClick = async () => {
    try {
      setIsSendingEmail(true);

      // Determine the sender email based on bdfCreatorId
      let emailTo = "sales@yellowspot.in";
      // Default sender email
      let emailFrom = "accounts@yellowspot.in";

      if (bdfCreatorId === "6655da0d21d3e92d1b400e27") {
        emailTo = "sales@yellowspot.in";
      } else if (bdfCreatorId === "6655d9b40777525410c88982") {
        emailTo = "aksha.shah@yellowspot.in";
      } else if (bdfCreatorId === "6655da0db7fa235637d8803a") {
        emailTo = "mediaplanning@yellowspot.in";
      } else if (bdfCreatorId === "668bb8a8dc1b17763bfc16bd") {
        emailTo = "pankaj.vyas@yellowspot.in";
      } else if (bdfCreatorId === "668bb8a98846df09d6649fc3") {
        emailTo = "meet.javia@yellowspot.in";
      } else if (bdfCreatorId === "668bb8a86a5e8031326ead83") {
        emailTo = "saurabh.sharma@yellowspot.in";
      } else if (bdfCreatorId === "6655da3e6826d0540967b9ce") {
        emailTo = "sagar.panchal@khushiadvertising.com";
      } else if (bdfCreatorId === "673720baba31e17dc04e3c4f") {
        emailTo = "dimple.pandya@khushiadvertising.com";
      }

      if (user?._id === "67b6eb62244cb99b99e46a9b") {
        emailFrom = "accounts@yellowspot.in";
      } else if (user?._id === "6746d54f4d93e350eadae03f") {
        emailFrom = "commercial@yellowspot.in";
      } else if (user?._id === "67b5c01e872dc76e3e364da3") {
        emailFrom = "rinku.mitra@yellowspot.in";
      }

      // Send the email
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/sendInvoiceMail`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            authorization: `Token ${window.localStorage.getItem("token")}`,
          },
          body: JSON.stringify({
            emailBody: bodyOfEmail,
            emailTo: emailTo,
            emailSubject: emailSubject,
            emailFrom: emailFrom,
          }),
        }
      );

      const responseJson = await response.json();

      if (!response.ok) {
        notification.error({
          message: responseJson.message || "Failed to send email",
        });
      } else {
        notification.success({
          message: responseJson.message || "Email sent successfully",
        });
        handleEmailModalClose();
      }
    } catch (err) {
      console.error("Error:", err);
      notification.error({ message: "Failed to send email" });
    } finally {
      setIsSendingEmail(false);
    }
  };

  return (
    <>
      <Modal
        title={<div style={{ marginBottom: "2rem" }}>Invoice Filters</div>}
        open={isOpen}
        onCancel={handleModalClose}
        width={600}
        footer={[
          asPerAnnexure && (
            <Button
              key="excel"
              type="primary"
              onClick={handleGenerateAnnexureExcel}
              style={{ marginRight: "8px" }}
            >
              <FaFileExcel style={{ marginRight: "8px" }} /> Annexure Excel
            </Button>
          ),
          <Button
            key="email"
            type="primary"
            onClick={handleSendEmail}
            style={{ marginRight: "8px" }}
          >
            <MdEmail style={{ marginRight: "8px" }} /> Send E-mail
          </Button>,
          <Button key="pdf" type="primary" onClick={handleGeneratePDF}>
            <BsDownload style={{ marginRight: "8px" }} /> PDF
          </Button>,
          <Button key="preview" type="primary" onClick={handlePreview}>
            <BsEye style={{ marginRight: "8px" }} /> Preview
          </Button>,
        ]}
      >
        {/* <div>
          <Checkbox
            checked={showHeading}
            onChange={(e) => setShowHeading(e.target.checked)}
            style={{ marginBottom: "10px" }}
          >
            Print Company Heading
          </Checkbox>
        </div> */}
        <div>
          <Checkbox
            checked={showLogo}
            onChange={(e) => setShowLogo(e.target.checked)}
            style={{ marginBottom: "10px" }}
          >
            Show logo
          </Checkbox>
        </div>
        <div>
          <Checkbox
            checked={showPeriod}
            onChange={(e) => setShowPeriod(e.target.checked)}
            style={{ marginBottom: "10px" }}
          >
            Show period
          </Checkbox>
        </div>
        <div>
          <Checkbox
            checked={showZeroAmountInventories}
            onChange={(e) => setShowZeroAmountInventories(e.target.checked)}
            style={{ marginBottom: "10px" }}
          >
            Show zero amount inventories
          </Checkbox>
        </div>

        <div>
          <Checkbox
            checked={asPerAnnexure}
            onChange={(e) => setAsPerAnnexure(e.target.checked)}
            style={{ marginBottom: "10px" }}
          >
            Show as per annexure
          </Checkbox>
        </div>

        <div>
          <Checkbox
            checked={showItemAmount}
            onChange={(e) => setShowItemAmount(e.target.checked)}
            style={{ marginBottom: "10px" }}
          >
            Show total billing amount only
          </Checkbox>
        </div>

        <div>
          <Checkbox
            checked={showSeparateProductionCharges}
            onChange={(e) => setShowSeparateProductionCharges(e.target.checked)}
            style={{ marginBottom: "10px" }}
          >
            Show printing and mounting separately
          </Checkbox>
        </div>

        <div>
          <Checkbox
            checked={showHoardingWisePrintingMounting}
            onChange={(e) =>
              setShowHoardingWisePrintingMounting(e.target.checked)
            }
            style={{ marginBottom: "10px" }}
          >
            Show hoarding wise printing & mounting
          </Checkbox>
        </div>

        <div>
          <Checkbox
            checked={showPrintingMountingOnly}
            onChange={(e) => {
              const checked = e.target.checked;
              setShowPrintingMountingOnly(checked);
              if (checked) {
                setAsPerAnnexure(false);
              }
            }}
            style={{ marginBottom: "10px" }}
          >
            Show printing & mounting only
          </Checkbox>
        </div>
      </Modal>

      {/* Simple ReactQuill Modal */}
      <Modal
        title="Email Content"
        open={isEmailModalOpen}
        onCancel={handleEmailModalClose}
        width={600}
        footer={[
          <Button key="cancel" onClick={handleEmailModalClose}>
            Cancel
          </Button>,
          <Button
            key="send"
            type="primary"
            onClick={handleSendEmailClick}
            loading={isSendingEmail}
          >
            Send
          </Button>,
        ]}
      >
        <div style={{ width: "500px", position: "relative", border: "0px" }}>
          <ReactQuill
            value={bodyOfEmail}
            onChange={handleTextArea}
            placeholder="Content of the body"
            modules={modules}
            formats={formats}
            style={{ height: "300px", border: "0px", outline: "none" }}
          />
          <style jsx>{`
            .ql-toolbar {
              position: absolute;
              bottom: -45px;
              left: 0;
              right: 0;
              width: 100%;
              display: flex;
              justify-content: center;
              border: 0px !important;
            }
            .ql-container {
              border: 0px !important;
            }
            .ql-editor {
              border: 0px !important;
              outline: none;
            }
          `}</style>
        </div>
      </Modal>

      <Modal
        title="Invoice Preview"
        open={isPreviewOpen}
        onCancel={() => setIsPreviewOpen(false)}
        footer={null}
        width="fit-content"
        centered
      >
        {isPreviewOpen && (
          <iframe
            ref={pdfPreviewRef}
            style={{
              width: "310mm", // A4 width
              height: "197mm", // A4 height
              border: "none",
              margin: "0 auto",
              display: "block",
              boxShadow: "0 0 15px rgba(0, 0, 0, 0.1)",
            }}
          />
        )}
      </Modal>
    </>
  );
};

export default GenerateSubInvoice;
